import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, AlertCircle, Upload, X, Building2, Star, MapPin, Award, Phone, Mail, Image as ImageIcon, Search, CheckCircle2 } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { trackNewOffer } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuggestedCompanyCard = ({ company }) => (
  <Link to={`/companies/${company.company_id}`} target="_blank" className="block">
    <div className="bg-white rounded-lg p-3 border border-orange-100 hover:border-orange-300 transition-colors">
      <div className="flex items-center gap-2 mb-2">
        {company.logo_url ? (
          <img src={company.logo_url} alt={company.name} className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
            <Building2 className="w-4 h-4 text-orange-500" />
          </div>
        )}
        <span className="font-medium text-sm text-slate-900 line-clamp-1">{company.name}</span>
      </div>
      {company.rating > 0 && (
        <div className="flex items-center gap-1">
          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
          <span className="text-xs text-slate-600">{company.rating.toFixed(1)}</span>
        </div>
      )}
      {company.location && (
        <div className="flex items-center gap-1 mt-1">
          <MapPin className="w-3 h-3 text-slate-400" />
          <span className="text-xs text-slate-500 line-clamp-1">{company.location}</span>
        </div>
      )}
    </div>
  </Link>
);

const SuggestionsSection = ({ category, companies, loading }) => {
  if (!category) return null;
  
  return (
    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
      <div className="flex items-center gap-2 mb-3">
        <Award className="w-5 h-5 text-orange-500" />
        <span className="font-medium text-orange-900">Polecane firmy w tej kategorii</span>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
        </div>
      ) : companies.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {companies.map((company) => (
            <SuggestedCompanyCard key={company.company_id} company={company} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-orange-700">
          Brak promowanych firm w tej kategorii. Dodaj zlecenie, a firmy same się do Ciebie zgłoszą!
        </p>
      )}
    </div>
  );
};

// Komponent dla zdjęcia z fallback
const GalleryImageItem = ({ src, index, onRemove }) => {
  const [hasError, setHasError] = useState(false);
  
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden group">
      {hasError || !src ? (
        <div className="w-full h-full bg-slate-100 flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-slate-300" />
        </div>
      ) : (
        <img 
          src={src} 
          alt={`Zdjęcie ${index + 1}`} 
          className="w-full h-full object-cover"
          onError={() => setHasError(true)}
        />
      )}
      <button 
        type="button"
        onClick={() => onRemove(index)} 
        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

const ImageGallery = ({ images, onRemove, onUpload, uploading, fileInputRef }) => (
  <div>
    <Label>Zdjęcia (opcjonalne)</Label>
    <p className="text-sm text-slate-500 mb-3">Dodaj zdjęcia miejsca pracy lub planów</p>
    
    <div className="grid grid-cols-4 gap-3 mb-3">
      {images.map((img, idx) => (
        <GalleryImageItem key={idx} src={img} index={idx} onRemove={onRemove} />
      ))}
      
      {images.length < 8 && (
        <button 
          type="button"
          onClick={() => fileInputRef.current?.click()} 
          disabled={uploading}
          className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-500 hover:border-orange-500 hover:text-orange-500 transition-colors"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <>
              <Upload className="w-6 h-6 mb-1" />
              <span className="text-xs">Dodaj</span>
            </>
          )}
        </button>
      )}
    </div>
    
    <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" />
  </div>
);

const NewOffer = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suggestedCompanies, setSuggestedCompanies] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    deadline: '',
    images: [],
    contact_phone: user?.phone || '',
    contact_email: user?.email || '',
  });
  const [confirmNotAd, setConfirmNotAd] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetch(`${API}/categories`)
      .then(res => res.ok ? res.json() : [])
      .then(setCategories)
      .catch(() => {});
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (formData.category) {
      setLoadingSuggestions(true);
      fetch(`${API}/suggested-companies/${formData.category}?limit=3`)
        .then(res => res.ok ? res.json() : [])
        .then(setSuggestedCompanies)
        .catch(() => setSuggestedCompanies([]))
        .finally(() => setLoadingSuggestions(false));
    } else {
      setSuggestedCompanies([]);
    }
  }, [formData.category]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const newImages = [...formData.images];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const res = await fetch(`${API}/upload/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
          body: formDataUpload,
        });

        if (res.ok) {
          const data = await res.json();
          // Supabase Storage zwraca pełny URL, nie dodajemy BACKEND_URL
          newImages.push(data.url);
        }
      } catch (error) {
        console.error(error);
      }
    }

    setFormData({ ...formData, images: newImages });
    setUploading(false);
  };

  const handleRemoveImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category || !formData.location) {
      toast.error('Wypełnij wszystkie wymagane pola');
      return;
    }

    if (!confirmNotAd) {
      toast.error('Potwierdź, że szukasz wykonawcy do swojego zlecenia');
      return;
    }

    if (!formData.contact_phone || !formData.contact_email) {
      toast.error('Numer telefonu i email są wymagane');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.contact_email)) {
      toast.error('Podaj poprawny adres email');
      return;
    }

    // Validate phone format (Polish format)
    const phoneRegex = /^[\d\s\-+()]{9,}$/;
    if (!phoneRegex.test(formData.contact_phone.replace(/\s/g, ''))) {
      toast.error('Podaj poprawny numer telefonu');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/offers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          category: formData.category,
          location: formData.location,
          deadline: formData.deadline || null,
          images: formData.images,
          contact_phone: formData.contact_phone,
          contact_email: formData.contact_email,
        }),
      });

      if (res.ok) {
        const offer = await res.json();
        
        // Śledź konwersję nowego ogłoszenia w Google Ads
        trackNewOffer(formData.category);
        
        toast.success('Zlecenie zostało dodane!');
        navigate(`/offers/${offer.offer_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Nie udało się dodać zlecenia');
      }
    } catch (error) {
      toast.error('Wystąpił błąd');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="new-offer-page">
      <div className="bg-slate-900 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />Powrót
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <Search className="w-8 h-8 text-orange-500" />
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">Szukam wykonawcy</h1>
          </div>
          <p className="text-slate-400 mt-2">Opisz jaką pracę potrzebujesz wykonać - firmy budowlane złożą Ci oferty</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Banner informacyjny */}
        <div className="mb-6 p-5 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200" data-testid="info-banner">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">
                📋 Tu dodajesz ZLECENIE dla wykonawców
              </h2>
              <p className="text-slate-700 mb-3">
                Opisz jaką pracę potrzebujesz wykonać (np. remont łazienki, wymiana okien, malowanie mieszkania). 
                Firmy budowlane zobaczą Twoje zlecenie i złożą oferty.
              </p>
              <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-orange-200">
                <Building2 className="w-5 h-5 text-orange-500" />
                <span className="text-sm text-slate-700">
                  <strong>Jesteś firmą budowlaną?</strong>{' '}
                  <Link to="/register" className="text-orange-600 hover:text-orange-700 underline font-medium">
                    Zarejestruj się jako firma
                  </Link>
                  {' '}i stwórz profesjonalny profil - klienci sami Cię znajdą!
                </span>
              </div>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Co potrzebujesz zrobić?</CardTitle>
            <CardDescription>Im więcej szczegółów podasz, tym lepsze oferty otrzymasz od firm</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Jakiej pracy szukasz? *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="np. Szukam wykonawcy do remontu łazienki 10m2"
                  required
                  data-testid="input-title"
                />
                <p className="text-xs text-slate-500 mt-1">Krótko opisz czego szukasz</p>
              </div>

              <div>
                <Label htmlFor="category">W jakiej kategorii szukasz wykonawcy? *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="input-category">
                    <SelectValue placeholder="Wybierz kategorię prac" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <SuggestionsSection category={formData.category} companies={suggestedCompanies} loading={loadingSuggestions} />

              <div>
                <Label htmlFor="description">Opisz szczegółowo jakie prace potrzebujesz wykonać *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Opisz szczegółowo zakres prac, które potrzebujesz wykonać, materiały, specjalne wymagania..."
                  rows={6}
                  required
                  data-testid="input-description"
                />
              </div>

              <div>
                <Label htmlFor="location">Gdzie potrzebujesz wykonawcy? (adres) *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="np. ul. Marszałkowska 1, Warszawa"
                  required
                  data-testid="input-location"
                />
                <p className="text-xs text-slate-500 mt-1">Podaj miasto lub dokładny adres - firmy z okolicy zobaczą Twoje zlecenie</p>
              </div>

              <div>
                <Label htmlFor="deadline">Do kiedy potrzebujesz wykonawcy?</Label>
                <Input
                  id="deadline"
                  name="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={handleChange}
                  data-testid="input-deadline"
                />
                <p className="text-xs text-slate-500 mt-1">Opcjonalne - podaj jeśli masz konkretny termin</p>
              </div>

              {/* Contact Information - Required */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-500" />
                  Dane kontaktowe (wymagane)
                </h3>
                <p className="text-sm text-blue-700 mb-4">
                  Dane kontaktowe będą widoczne tylko dla firm z aktywną subskrypcją.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_phone">Numer telefonu *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="contact_phone"
                        name="contact_phone"
                        value={formData.contact_phone}
                        onChange={handleChange}
                        placeholder="np. 123 456 789"
                        className="pl-10"
                        required
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="contact_email">Adres email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="contact_email"
                        name="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={handleChange}
                        placeholder="np. jan@example.com"
                        className="pl-10"
                        required
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <ImageGallery 
                images={formData.images}
                onRemove={handleRemoveImage}
                onUpload={handleImageUpload}
                uploading={uploading}
                fileInputRef={fileInputRef}
              />

              <div className="flex items-center gap-2 p-4 bg-yellow-50 rounded-md text-yellow-700 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>Twoje zlecenie będzie widoczne dla wszystkich. Dane kontaktowe zobaczą tylko firmy z subskrypcją.</span>
              </div>

              {/* Checkbox potwierdzający */}
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200" data-testid="confirm-checkbox-section">
                <Checkbox 
                  id="confirmNotAd" 
                  checked={confirmNotAd}
                  onCheckedChange={setConfirmNotAd}
                  className="mt-0.5 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                  data-testid="confirm-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="confirmNotAd" className="font-medium text-green-900 cursor-pointer flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Potwierdzam, że szukam wykonawcy do mojego zlecenia
                  </label>
                  <p className="text-sm text-green-700 mt-1">
                    To nie jest reklama usług - opisuję pracę, którą potrzebuję wykonać
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>Anuluj</Button>
                <Button type="submit" disabled={loading} className="bg-orange-500 hover:bg-orange-600" data-testid="submit-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Dodawanie...</> : 'Dodaj zlecenie'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewOffer;
